import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fetchStaffSession, signOut, type StaffSessionView } from "@/lib/auth";
import { LOGIN_PATH, isLoginPath, loginLocation, pathAfterLogin } from "@/lib/session-gate";

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const signingOutRef = useRef(false);
  const [session, setSession] = useState<StaffSessionView | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const refreshSession = useCallback(async () => {
    const next = await fetchStaffSession();
    setSession(next);
    setSessionLoaded(true);
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  async function onLogout() {
    signingOutRef.current = true;
    await signOut();
    setSession(null);
    navigate(LOGIN_PATH, { replace: true });
  }

  const onLogin = isLoginPath(location.pathname);

  if (!sessionLoaded) {
    return (
      <div className="bg-background min-h-screen">
        <header className="border-b">
          <div className="container flex h-14 items-center gap-6">
            <span className="font-semibold tracking-tight">ByteBite Dashboard</span>
          </div>
        </header>
        <main className="container py-8">
          <p className="text-muted-foreground text-sm">Loading session…</p>
        </main>
      </div>
    );
  }

  if (!session && !onLogin) {
    const to = signingOutRef.current ? LOGIN_PATH : loginLocation(location.pathname, location.search);
    return <Navigate to={to} replace />;
  }

  if (session && onLogin) {
    return <Navigate to={pathAfterLogin(location.search)} replace />;
  }

  signingOutRef.current = false;

  return (
    <div className="bg-background min-h-screen">
      <header className="border-b">
        <div className="container flex h-14 items-center gap-6">
          <span className="font-semibold tracking-tight">ByteBite Dashboard</span>
          {session ? (
            <nav className="flex items-center gap-4 text-sm">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Restaurant
              </NavLink>
              <NavLink
                to="/menu"
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Menu
              </NavLink>
              <NavLink
                to="/orders"
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Orders
              </NavLink>
              <NavLink
                to="/delivery"
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Delivery
              </NavLink>
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Admin
              </NavLink>
            </nav>
          ) : null}
          <div className="ml-auto flex items-center gap-3 text-sm">
            {session ? (
              <>
                <span className="text-muted-foreground">
                  {session.email} · {session.role}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => void onLogout()}>
                  Sign out
                </Button>
              </>
            ) : (
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                }
              >
                Sign in
              </NavLink>
            )}
          </div>
        </div>
      </header>
      <main className="container py-8">
        <Outlet context={{ session, refreshSession }} />
      </main>
    </div>
  );
}
